import React, { useState, useEffect } from 'react';
import { ArrowUp, X } from 'lucide-react';
import { sendQuestionChat } from '../../api/exams';
import Mina from '../../assets/images/mina.png';
import './AskMinaChat.css';

// CSS for mobile Ask Mina input styling - FIXED to prevent zoom
const mobileInputStyles = `
@media (max-width: 768px) {
  .context-chat-input-container input {
    font-size: 16px !important;
    font-family: 'Manrope', sans-serif !important;
    transform: translateZ(0);
    -webkit-appearance: none;
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    zoom: 1;
  }
  
  .context-chat-input-container input::placeholder {
    font-size: 16px !important;
    font-family: 'Manrope', sans-serif !important;
  }
}
`;

// Function to format chat messages with bold text and bullet points
const formatChatMessage = (text) => {
  if (!text) return '';
  
  // First, identify bullet point sequences
  const lines = text.split('\n');
  const processedLines = [];
  let bulletList = null;
  
  lines.forEach((line, index) => {
    // Check if this line is a bullet point (starts with * or - followed by space)
    if (line.trim().match(/^(\*|-)\s+(.+)$/)) {
      // If we don't have an active bullet list, start one
      if (!bulletList) {
        bulletList = [];
      }
      
      // Add this item to the bullet list
      bulletList.push(line.trim().replace(/^(\*|-)\s+/, ''));
    } else {
      // This is not a bullet point
      
      // If we have an active bullet list, add it to the processed lines and reset
      if (bulletList && bulletList.length > 0) {
        processedLines.push({
          type: 'bulletList',
          items: [...bulletList]
        });
        bulletList = null;
      }
      
      // Add the regular line
      processedLines.push({
        type: 'text',
        content: line
      });
    }
  });
  
  // If we have a bullet list at the end, add it
  if (bulletList && bulletList.length > 0) {
    processedLines.push({
      type: 'bulletList',
      items: [...bulletList]
    });
  }
  
  // Now render the processed lines
  return (
    <div>
      {processedLines.map((item, index) => {
        if (item.type === 'bulletList') {
          // Render bullet list
          return (
            <ul key={`list-${index}`} style={{ 
              paddingLeft: '20px',
              margin: '0.5em 0',
              listStyleType: 'disc'
            }}>
              {item.items.map((bulletItem, bulletIndex) => (
                <li key={`bullet-${index}-${bulletIndex}`}>
                  {formatBoldText(bulletItem)}
                </li>
              ))}
            </ul>
          );
        } else {
          // Render regular text
          return (
            <div key={`text-${index}`} style={{ margin: '0.5em 0' }}>
              {formatBoldText(item.content)}
            </div>
          );
        }
      })}
    </div>
  );
};

// Helper function to format bold and italic text
const formatBoldText = (text) => {
  const parts = [];
  let lastIndex = 0;
  
  // Process the text with a combined function to handle both bold and italic formatting
  const processText = (currentText, startIndex) => {
    let currentIndex = startIndex;
    
    // First check for bold (**text**)
    const boldPattern = /\*\*(.*?)\*\*/g;
    boldPattern.lastIndex = currentIndex;
    
    const boldMatch = boldPattern.exec(currentText);
    
    // Then check for italic (*text*) - but ensure it's not part of ** pattern
    const italicPattern = /(?<!\*)\*((?!\*).+?)\*(?!\*)/g;
    italicPattern.lastIndex = currentIndex;
    
    const italicMatch = italicPattern.exec(currentText);
    
    // Determine which pattern matched first (if any)
    if (boldMatch && italicMatch) {
      if (boldMatch.index < italicMatch.index) {
        return processBold(currentText, currentIndex, boldMatch);
      } else {
        return processItalic(currentText, currentIndex, italicMatch);
      }
    } else if (boldMatch) {
      return processBold(currentText, currentIndex, boldMatch);
    } else if (italicMatch) {
      return processItalic(currentText, currentIndex, italicMatch);
    } else {
      // No more formatting found
      if (currentIndex < currentText.length) {
        parts.push(currentText.substring(currentIndex));
      }
      return currentText.length;
    }
  };
  
  // Process bold text
  const processBold = (text, currentIndex, match) => {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }
    
    // Add the bold text
    parts.push(
      <strong key={`bold-${match.index}`}>
        {match[1]}
      </strong>
    );
    
    return match.index + match[0].length;
  };
  
  // Process italic text
  const processItalic = (text, currentIndex, match) => {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }
    
    // Add the italic text
    parts.push(
      <em key={`italic-${match.index}`}>
        {match[1]}
      </em>
    );
    
    return match.index + match[0].length;
  };
  
  // Process the text iteratively
  while (lastIndex < text.length) {
    lastIndex = processText(text, lastIndex);
  }
  
  return parts.length > 0 ? parts : text;
};

/**
 * AskMinaChat Component
 * A universal reusable chat component for interacting with Mina AI
 * Works for desktop and mobile, personalized exams, listening challenges, and review modes
 * 
 * Props:
 * - visible: boolean - Whether the chat is visible
 * - onClose: function - Callback when chat is closed
 * - questionContext: object - Context about the current question(s)
 * - questionSet: array - Array of related questions (for multiple questions)
 * - answeredQuestions: object - Object mapping question IDs to selected answers
 * - title: string - Title for the chat header (default: "Chat with Mina")
 * - className: string - Additional CSS classes
 * - resetOnClose: boolean - Whether to reset messages when chat closes (default: true)
 * - onSendMessage: function - Custom API handler function (optional, uses default if not provided)
 */
const AskMinaChat = ({ 
  visible, 
  onClose, 
  questionContext, 
  questionSet = [], 
  answeredQuestions = {},
  title = "Chat with Mina",
  className = "",
  resetOnClose = true,
  onSendMessage = null
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Inject mobile CSS styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = mobileInputStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Initialize with welcome message when chat opens
  useEffect(() => {
    if (visible && messages.length === 0) {
      setMessages([
        { id: 1, sender: 'bot', text: 'How can I help you with this question?' }
      ]);
    }
  }, [visible, messages.length]);

  // Handle closing the chat
  const handleCloseChat = () => {
    if (resetOnClose) {
      setMessages([]);
      setInputValue('');
      setIsTyping(false);
    }
    onClose();
  };

  // Handle sending message to chat
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isTyping) return;
    
    // Add user message
    const userInput = inputValue;
    const userMessage = { id: Date.now(), sender: 'user', text: userInput };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    try {
      // If a custom onSendMessage function is provided, use it
      if (onSendMessage) {
        const response = await onSendMessage({
          userInput,
          questionContext,
          questionSet,
          answeredQuestions,
          conversationHistory: [...messages, userMessage].map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'Mina',
            content: msg.text
          }))
        });
        
        // Add bot response
        const botMessage = { 
          id: Date.now(), 
          sender: 'bot', 
          text: response
        };
        setMessages(prevMessages => [...prevMessages, botMessage]);
        return;
      }
      
      // Default API logic (for PersonalizedExam context)
      // Get the current question (first question if multiple)
      const currentQuestion = questionSet.length > 0 ? questionSet[0] : questionContext;
      
      // Get the user's selected answer index
      const answerIndex = answeredQuestions[currentQuestion?.question_id];
      
      // Get the user's selected answer text
      const userAnswer = answerIndex !== undefined 
        ? currentQuestion?.choices[answerIndex] 
        : null;
      
      console.log('User selected answer:', userAnswer);
      
      // Format the chat history for the API (include ALL messages including current one)
      const conversationHistory = [...messages, userMessage]
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'Mina',
          content: msg.text
        }));
      
      // Prepare question context for the API - include all related questions if available
      const allQuestionsInSet = questionSet && questionSet.length > 1 ? questionSet : [currentQuestion];
      
      const apiQuestionContext = {
        question_id: currentQuestion?.question_id,
        question_text: currentQuestion?.question,
        options: currentQuestion?.choices,
        correct_answer: currentQuestion?.correct_answer,
        explanation: currentQuestion?.explanation,
        user_answer: userAnswer,
        // Include all related questions for better AI context
        all_questions: allQuestionsInSet.map(q => {
          const qAnswerIndex = answeredQuestions[q.question_id];
          const qUserAnswer = qAnswerIndex !== undefined ? q.choices[qAnswerIndex] : null;
          return {
            question_id: q.question_id,
            question_text: q.question,
            options: q.choices,
            correct_answer: q.correct_answer,
            explanation: q.explanation,
            user_answer: qUserAnswer,
            script: q.script
          };
        }),
        script: currentQuestion?.script,
        image_path: currentQuestion?.script_image_url || 
                  (currentQuestion?.script_image?.urls?.[0]) ||
                  (currentQuestion.related && Array.isArray(currentQuestion.related) && currentQuestion.related.length > 0 ?
                    `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.related[0]}.png` :
                    (currentQuestion.ref_id ?
                      `/images/${currentQuestion.level || "N5"}/reading/script/${currentQuestion.ref_id}.png` :
                      null))
      };
      
      console.log('Question context:', apiQuestionContext);
      
      let responseData;
      
      try {
        // Try to use our updated sendQuestionChat API with the right parameters
        responseData = await sendQuestionChat(apiQuestionContext, userInput, conversationHistory);
        console.log('API response:', responseData);
      } catch (apiError) {
        console.error('Error calling API, falling back to mock response:', apiError);
        
        // If the API call fails, provide a meaningful fallback response
        let mockContent = `I'll help explain this question. The question is asking about "${apiQuestionContext.question_text}".`;
        
        if (apiQuestionContext.options) {
          mockContent += `\n\nThe correct answer is "${apiQuestionContext.correct_answer}".`;
          
          if (apiQuestionContext.user_answer) {
            mockContent += `\n\nYou selected "${apiQuestionContext.user_answer}", which is ${
              apiQuestionContext.user_answer === apiQuestionContext.correct_answer ? 'correct!' : 'incorrect.'
            }`;
          }
        }
        
        if (apiQuestionContext.explanation) {
          mockContent += `\n\n${apiQuestionContext.explanation}`;
        } else {
          mockContent += `\n\nThere is no additional explanation provided for this question. Would you like me to analyze it further?`;
        }
        
        responseData = {
          choices: [{
            message: {
              content: mockContent
            }
          }]
        };
      }
      
      // Extract the bot's response text - handle different response formats
      let botResponseText;
      
      // Check if the response contains a JSON part
      if (typeof responseData === 'string') {
        // If the response is a string, try to split out any JSON part
        const jsonStartIndex = responseData.indexOf('{');
        if (jsonStartIndex > 0) {
          // Extract the text part before the JSON
          botResponseText = responseData.substring(0, jsonStartIndex).trim();
        } else {
          botResponseText = responseData;
        }
      } else if (responseData.choices && responseData.choices[0] && responseData.choices[0].message) {
        // OpenAI-like response format
        botResponseText = responseData.choices[0].message.content;
      } else if (responseData.response) {
        // Our custom API response format
        botResponseText = responseData.response;
      } else if (responseData.text) {
        // Alternative response format
        botResponseText = responseData.text;
      } else if (responseData.history) {
        // If we have a history array from the backend
        const systemMessages = responseData.history.filter(msg => msg.role === 'system');
        // Get the last system message which should be the AI response
        if (systemMessages.length > 0) {
          botResponseText = systemMessages[systemMessages.length - 1].content;
        } else {
          botResponseText = "I'm sorry, I couldn't process your question properly.";
        }
      } else {
        // Fallback if the response format is unexpected
        botResponseText = "I'm sorry, but I couldn't process your question properly. Please try asking something else.";
      }
      
      // Add bot message with the AI response
      const botMessage = { 
        id: Date.now(), 
        sender: 'bot', 
        text: botResponseText
      };
      setMessages(prevMessages => [...prevMessages, botMessage]);
    } catch (error) {
      console.error('Error processing chat message:', error);
      // Fallback response in case of error
      const errorMessage = { 
        id: Date.now(), 
        sender: 'bot', 
        text: 'I apologize, but I encountered an error while processing your question. Please try again later.' 
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Context Chat Component */}
      <div className={`context-chat visible ${className}`}>
        <div className="context-chat-header">
          <h3>{title}</h3>
          <button 
            className="context-chat-close" 
            onClick={handleCloseChat}
            aria-label="Close chat"
          >
            <X size={20} />
          </button>
        </div>

        <div className="context-chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`context-chat-message ${message.sender}`}>
              {message.sender === 'bot' && (
                <div className="bot-avatar">
                  <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
                </div>
              )}
              <div className="message-content">
                {formatChatMessage(message.text)}
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="context-chat-message bot">
              <div className="bot-avatar">
                <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
              </div>
              <div className="message-content">
                <span className="typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="context-chat-input-container">
          <form onSubmit={handleSendMessage}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              disabled={isTyping}
            />
            <button 
              type="submit" 
              className="send-message" 
              disabled={!inputValue.trim() || isTyping}
              aria-label="Send message"
            >
              <ArrowUp size={20} />
            </button>
          </form>
        </div>
      </div>
      
      {/* Chat Overlay */}
      <div 
        className={`context-chat-overlay visible`}
        onClick={handleCloseChat}
      />
    </>
  );
};

export default AskMinaChat;